import type {
  AxChatLogEntry,
  AxGenIn,
  AxProgramForwardOptions,
  AxProgramUsage,
} from '../../dsp/types.js';
import { AxAIServiceAbortedError } from '../../util/apicall.js';
import type {
  AxAgentGuidancePayload,
  AxAgentInternalCompletionPayload,
} from '../completion.js';
import {
  computeEffectiveChatBudget,
  getActorModelConsecutiveErrorTurns,
  getActorModelMatchedNamespaces,
  selectActorModelFromPolicy,
} from '../config.js';
import {
  classifyContextPressure,
  emitContextEvent,
  renderContextPressure,
} from '../contextEvents.js';
import { manageContext } from '../contextManager.js';
import { normalizeActorCode } from '../optimize.js';
import {
  formatBubbledActorTurnOutput,
  validateActorTurnCodePolicy,
} from '../runtime.js';
import type { ActorLoopContext } from './actorLoopContext.js';
import {
  appendDiscoveryTurnSummary,
  stripDiscoveryTurnOutput,
} from './discoveryHelpers.js';
import {
  appendGuidanceEntry,
  buildGuidanceActionLogCode,
  buildGuidanceActionLogOutput,
  renderGuidanceLog,
  snapshotChatLogMessages,
} from './guidanceHelpers.js';
import { AxAgentClarificationError } from './types.js';

const ACTOR_CODE_POLICY_GUIDANCE =
  'Your previous Javascript Code value did not satisfy the executable-code turn contract. ' +
  'On this turn, set Javascript Code to runnable JavaScript only: use console.log(...) for inspection, ' +
  'await final("...", { ... }) when complete, or await askClarification(...) when blocked. ' +
  'Do not emit plain task:/evidence: labels or prose as the Javascript Code value.';

function buildMultipleCodeBlocksPolicyViolation(
  runtimeCodeFieldTitle: string
): string {
  return `[POLICY] ${runtimeCodeFieldTitle} must contain at most one fenced code block. No code from the previous turn was executed.`;
}

function buildMultipleCodeBlocksPolicyGuidance(
  runtimeCodeFieldTitle: string
): string {
  return (
    `Your previous ${runtimeCodeFieldTitle} value contained multiple fenced code blocks, so none of them were executed. ` +
    `On this turn, put every executable statement in one ${runtimeCodeFieldTitle} value with at most one fence.`
  );
}

function extractRawActorCode(
  chatLog: readonly AxChatLogEntry[],
  runtimeCodeFieldTitle: string
): string | undefined {
  const messages = chatLog[chatLog.length - 1]?.messages;
  if (!messages) {
    return undefined;
  }

  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (message?.role !== 'assistant') {
      continue;
    }

    const content = message.content
      .replace(/<think>[\s\S]*?<\/think>/g, '')
      .replace(/[^\n]*<\/think>/g, '')
      .trim();

    const prefix = `${runtimeCodeFieldTitle}:`;
    const prefixIndex = content.indexOf(prefix);
    if (prefixIndex >= 0) {
      return content.slice(prefixIndex + prefix.length).trim();
    }
    return content;
  }

  return undefined;
}

function containsMultipleFencedCodeBlocks(code: string): boolean {
  let insideFence = false;
  let blockCount = 0;

  for (const line of code.split(/\r?\n/)) {
    // Match the same triple-backtick opener shape accepted by
    // normalizeActorCode, including prose before the first fence.
    const fence = line.match(/```([A-Za-z0-9_-]+)?[ \t]*$/);
    if (!fence) {
      continue;
    }

    const hasLanguage = fence[1] !== undefined;
    if (insideFence && !hasLanguage) {
      insideFence = false;
      continue;
    }

    blockCount++;
    if (blockCount > 1) {
      return true;
    }
    insideFence = true;
  }

  return false;
}

export async function runActorTurn<_IN extends AxGenIn>(
  ctx: ActorLoopContext,
  turn: number,
  _options: Readonly<AxProgramForwardOptions<string>> | undefined,
  _effectiveAbortSignal: AbortSignal | undefined,
  applyInputUpdateCallback: () => Promise<void>,
  _maxTurns: number
): Promise<{ shouldBreak: boolean; shouldContinue: boolean }> {
  const {
    s,
    ai,
    rlm,
    runtimeContext,
    inputState,
    completionState,
    guidanceState,
    actionLogEntries,
    actorMergedOptions,
    summaryForwardOptions,
    functionCallRecords,
    explicitActorDebugHideSystemPrompt,
    contextStage,
    contextThreshold,
    mutableState,
    helpers,
  } = ctx;

  const {
    refreshActorInstruction,
    buildActorPromptValues,
    measureActorPromptChars,
    renderActionLogParts,
    renderActionLogPartsWithReplayMode,
    resetActorModelErrorState,
    noteActorTurnErrorState,
    syncDiscoveredActorModelNamespaces,
    refreshCheckpointSummary,
  } = helpers;

  const actorInstruction = refreshActorInstruction();
  await applyInputUpdateCallback();
  inputState.recomputeTurnInputs(true);
  for (const promotion of inputState.drainAutoPromotionEvents()) {
    await emitContextEvent(s.onContextEvent, {
      kind: 'field_auto_promoted',
      stage: contextStage,
      turn: turn + 1,
      ...promotion,
    });
  }
  if (await refreshCheckpointSummary(actionLogEntries.length)) {
    resetActorModelErrorState();
  }

  let actionLogParts = renderActionLogParts();
  let summarizedActorLogText = actionLogParts.summary || undefined;
  let actionLogText = actionLogParts.history || '(no actions yet)';
  const guidanceLogText = renderGuidanceLog(guidanceState.entries);
  let inspectMetrics = await measureActorPromptChars(
    actionLogText,
    guidanceLogText,
    mutableState.runtimeStateSummary,
    summarizedActorLogText
  );
  let inspectFixedOverhead =
    inspectMetrics.systemPromptCharacters +
    inspectMetrics.exampleChatContextCharacters;
  let effectiveBudgetChars = computeEffectiveChatBudget(
    runtimeContext.effectiveContextConfig.targetPromptChars,
    inspectFixedOverhead
  );
  const checkpointActive = Boolean(mutableState.checkpointState);
  let pressure = classifyContextPressure({
    mutablePromptChars: inspectMetrics.mutableChatContextCharacters,
    effectiveBudgetChars,
    checkpointActive,
  });
  const pressureHygieneMode =
    runtimeContext.effectiveContextConfig.contextHygiene?.pressureMode;
  const defaultHygieneMode =
    runtimeContext.effectiveContextConfig.contextHygiene?.defaultMode ?? 'none';
  if (
    pressure !== 'ok' &&
    pressureHygieneMode &&
    pressureHygieneMode !== defaultHygieneMode
  ) {
    const cps = mutableState.checkpointState;
    const pressureParts = renderActionLogPartsWithReplayMode(
      runtimeContext.effectiveContextConfig.actionReplay,
      cps?.summary,
      cps?.turns,
      pressureHygieneMode
    );
    const pressureActionLogText = pressureParts.history || '(no actions yet)';
    const pressureSummarizedActorLogText = pressureParts.summary || undefined;
    const pressureMetrics = await measureActorPromptChars(
      pressureActionLogText,
      guidanceLogText,
      mutableState.runtimeStateSummary,
      pressureSummarizedActorLogText
    );
    if (
      pressureMetrics.mutableChatContextCharacters <
      inspectMetrics.mutableChatContextCharacters
    ) {
      actionLogParts = pressureParts;
      actionLogText = pressureActionLogText;
      summarizedActorLogText = pressureSummarizedActorLogText;
      inspectMetrics = pressureMetrics;
      inspectFixedOverhead =
        inspectMetrics.systemPromptCharacters +
        inspectMetrics.exampleChatContextCharacters;
      effectiveBudgetChars = computeEffectiveChatBudget(
        runtimeContext.effectiveContextConfig.targetPromptChars,
        inspectFixedOverhead
      );
      pressure = classifyContextPressure({
        mutablePromptChars: inspectMetrics.mutableChatContextCharacters,
        effectiveBudgetChars,
        checkpointActive,
      });
    }
  }
  const contextPressureText =
    runtimeContext.effectiveContextConfig.preset !== 'full'
      ? renderContextPressure(pressure)
      : undefined;
  for (const compaction of actionLogParts.compactions) {
    await emitContextEvent(s.onContextEvent, {
      kind: 'action_compacted',
      stage: contextStage,
      turn: compaction.turn,
      mode: compaction.mode,
      reason: compaction.reason,
      originalChars: compaction.originalChars,
      renderedChars: compaction.renderedChars,
    });
  }
  await emitContextEvent(s.onContextEvent, {
    kind: 'budget_check',
    stage: contextStage,
    turn: turn + 1,
    pressure,
    mutablePromptChars: inspectMetrics.mutableChatContextCharacters,
    fixedPromptChars: inspectFixedOverhead,
    effectiveBudgetChars,
    targetPromptChars: runtimeContext.effectiveContextConfig.targetPromptChars,
    checkpointActive,
    actionLogEntryCount: actionLogEntries.length,
    guidanceLogEntryCount: guidanceState.entries.length,
  });
  if (
    contextThreshold &&
    inspectMetrics.mutableChatContextCharacters >
      computeEffectiveChatBudget(contextThreshold, inspectFixedOverhead)
  ) {
    actionLogText +=
      '\n\n[HINT: Actor prompt is large. Call `const state = await inspectRuntime()` for a compact snapshot of current variables instead of re-reading old outputs.]';
  }

  let actorCallOptions = actorMergedOptions;
  if (s.executorModelPolicy) {
    syncDiscoveredActorModelNamespaces();
    const selectedModel = selectActorModelFromPolicy(
      s.executorModelPolicy,
      getActorModelConsecutiveErrorTurns(mutableState.actorModelState),
      getActorModelMatchedNamespaces(mutableState.actorModelState)
    );
    actorCallOptions =
      selectedModel !== undefined
        ? {
            ...actorMergedOptions,
            model: selectedModel,
          }
        : actorMergedOptions;
  }

  const debugHideSystemPrompt =
    explicitActorDebugHideSystemPrompt ??
    (turn > 0 &&
      actorInstruction === mutableState.lastDebugLoggedActorInstruction);
  actorCallOptions = {
    ...actorCallOptions,
    debugHideSystemPrompt,
    functions: runtimeContext.getNativeFunctions?.(actorCallOptions),
  };

  const usageBefore = s.actorProgram.getUsage()?.length ?? 0;
  const actorTurnCallback = rlm.actorTurnCallback;

  const executorResult = await s.actorProgram.forward(
    ai,
    buildActorPromptValues(
      actionLogText,
      guidanceLogText,
      mutableState.runtimeStateSummary,
      summarizedActorLogText,
      contextPressureText
    ),
    actorCallOptions
  );
  if (!debugHideSystemPrompt) {
    mutableState.lastDebugLoggedActorInstruction = actorInstruction;
  }

  // Capture per-turn metadata for the callback.
  const turnUsage = actorTurnCallback
    ? (s.actorProgram.getUsage()?.slice(usageBefore) as
        | AxProgramUsage[]
        | undefined)
    : undefined;
  const turnModel =
    actorCallOptions.model !== undefined
      ? String(actorCallOptions.model)
      : undefined;
  const actorChatLog = s.actorProgram.getChatLog();
  const turnChatLogMessages = actorTurnCallback
    ? snapshotChatLogMessages(actorChatLog)
    : undefined;

  if (turn === 0) {
    mutableState.restoreNotice = undefined;
  }

  const runtimeCodeFieldName = s.runtimeCodeFieldName ?? 'javascriptCode';
  const runtimeCodeFieldTitle = s.runtimeCodeFieldTitle ?? 'Javascript Code';
  let code = executorResult[runtimeCodeFieldName] as string | undefined;
  const trimmedCode = code?.trim();
  // Code-field parsing extracts the first Markdown fence, so inspect the raw
  // response before a later block can disappear from the actor turn.
  const rawResponseCode = extractRawActorCode(
    actorChatLog,
    runtimeCodeFieldTitle
  )?.trim();
  const codeBeforeNormalization = rawResponseCode ?? trimmedCode;
  const hasMultipleFencedCodeBlocks =
    typeof codeBeforeNormalization === 'string' &&
    containsMultipleFencedCodeBlocks(codeBeforeNormalization);
  if (hasMultipleFencedCodeBlocks) {
    code = codeBeforeNormalization;
  } else {
    if (!code || !trimmedCode) {
      return { shouldBreak: true, shouldContinue: false };
    }
    code = normalizeActorCode(trimmedCode);
  }
  executorResult[runtimeCodeFieldName] = code;

  completionState.payload = undefined;
  const functionCallStartIndex = functionCallRecords?.length ?? 0;

  if (hasMultipleFencedCodeBlocks || s.enforceIncrementalConsoleTurns) {
    const policyResult = hasMultipleFencedCodeBlocks
      ? {
          violation: buildMultipleCodeBlocksPolicyViolation(
            runtimeCodeFieldTitle
          ),
        }
      : validateActorTurnCodePolicy(code);

    // Auto-split: discovery mixed with other code — run discovery first,
    // then proceed to execute the full code block (discovery calls are
    // idempotent so re-running is safe).
    if (policyResult?.autoSplitDiscoveryCode) {
      await runtimeContext.executeActorCode(
        policyResult.autoSplitDiscoveryCode
      );
    }

    if (policyResult?.violation) {
      const policyViolation = policyResult.violation;
      const entryTurn = actionLogEntries.length + 1;
      appendGuidanceEntry(guidanceState.entries, {
        turn: entryTurn,
        guidance: hasMultipleFencedCodeBlocks
          ? buildMultipleCodeBlocksPolicyGuidance(runtimeCodeFieldTitle)
          : ACTOR_CODE_POLICY_GUIDANCE,
        triggeredBy: 'runtime policy',
      });
      actionLogEntries.push({
        turn: entryTurn,
        code,
        output: [
          ...(runtimeContext.consumeNativeToolActivity?.() ?? []),
          policyViolation,
        ].join('\n'),
        tags: ['error'],
        ...(() => {
          const calls =
            functionCallRecords?.slice(functionCallStartIndex) ?? [];
          return calls.length > 0 ? { _functionCalls: calls } : {};
        })(),
      });

      if (actorTurnCallback) {
        await actorTurnCallback({
          stage: contextStage,
          turn: entryTurn,
          actionLogEntryCount: actionLogEntries.length,
          guidanceLogEntryCount: guidanceState.entries.length,
          executorResult: executorResult as Record<string, unknown>,
          code,
          result: undefined,
          output: policyViolation,
          isError: true,
          thought:
            typeof executorResult.thought === 'string'
              ? executorResult.thought
              : undefined,
          usage: turnUsage,
          model: turnModel,
          chatLogMessages: turnChatLogMessages,
        });
      }

      await manageContext(
        actionLogEntries,
        actionLogEntries.length - 1,
        runtimeContext.effectiveContextConfig,
        ai,
        summaryForwardOptions,
        { stage: contextStage, onContextEvent: s.onContextEvent }
      );
      noteActorTurnErrorState(true);
      if (await refreshCheckpointSummary(entryTurn)) {
        resetActorModelErrorState();
      }
      return { shouldBreak: false, shouldContinue: true };
    }
  }

  if (s.inputUpdateCallback) {
    await runtimeContext.syncRuntimeInputsToSession();
  }
  let result: unknown;
  let output: string;
  let isError: boolean;

  try {
    const executionResult = await runtimeContext.executeActorCode(code);
    result = executionResult.result;
    output = executionResult.output;
    isError = executionResult.isError;
  } catch (err) {
    if (
      err instanceof AxAgentClarificationError ||
      err instanceof AxAIServiceAbortedError ||
      s.shouldBubbleUserError(err)
    ) {
      const bubbledError = err instanceof Error ? err : new Error(String(err));
      if (actorTurnCallback) {
        await actorTurnCallback({
          stage: contextStage,
          turn: actionLogEntries.length + 1,
          actionLogEntryCount: actionLogEntries.length,
          guidanceLogEntryCount: guidanceState.entries.length,
          executorResult: executorResult as Record<string, unknown>,
          code,
          result: undefined,
          output: formatBubbledActorTurnOutput(
            bubbledError,
            runtimeContext.effectiveContextConfig.maxRuntimeChars
          ),
          isError:
            err instanceof AxAIServiceAbortedError ||
            s.shouldBubbleUserError(err),
          thought:
            typeof executorResult.thought === 'string'
              ? executorResult.thought
              : undefined,
          usage: turnUsage,
          model: turnModel,
          chatLogMessages: turnChatLogMessages,
        });
      }
    }
    throw err;
  }

  const completionPayload = completionState.payload as
    | AxAgentInternalCompletionPayload
    | undefined;
  const guidancePayload =
    completionPayload?.type === 'guide_agent'
      ? (completionPayload as AxAgentGuidancePayload)
      : undefined;
  if (guidancePayload) {
    const nextTurn = actionLogEntries.length + 1;
    appendGuidanceEntry(guidanceState.entries, {
      turn: nextTurn,
      guidance: guidancePayload.guidance,
      ...(guidancePayload.triggeredBy
        ? { triggeredBy: guidancePayload.triggeredBy }
        : {}),
    });
    result = undefined;
    output = buildGuidanceActionLogOutput(guidancePayload);
    isError = false;
  }

  const discoveryTurnArtifacts = runtimeContext.consumeDiscoveryTurnArtifacts();
  if (!isError) {
    output = stripDiscoveryTurnOutput(output, discoveryTurnArtifacts.texts);
    output = appendDiscoveryTurnSummary(output, discoveryTurnArtifacts.summary);
  }

  output = [
    ...(runtimeContext.consumeNativeToolActivity?.() ?? []),
    output,
  ].join('\n');
  const entryTurn = actionLogEntries.length + 1;
  const actionLogCode = guidancePayload
    ? buildGuidanceActionLogCode(guidancePayload)
    : code;
  actionLogEntries.push({
    turn: entryTurn,
    code: actionLogCode,
    output,
    tags: isError ? ['error'] : [],
    ...(() => {
      const calls = functionCallRecords?.slice(functionCallStartIndex) ?? [];
      return calls.length > 0 ? { _functionCalls: calls } : {};
    })(),
  });

  if (actorTurnCallback) {
    await actorTurnCallback({
      stage: contextStage,
      turn: entryTurn,
      actionLogEntryCount: actionLogEntries.length,
      guidanceLogEntryCount: guidanceState.entries.length,
      executorResult: executorResult as Record<string, unknown>,
      code,
      result,
      output,
      isError,
      thought:
        typeof executorResult.thought === 'string'
          ? executorResult.thought
          : undefined,
      usage: turnUsage,
      model: turnModel,
      chatLogMessages: turnChatLogMessages,
    });
  }

  await manageContext(
    actionLogEntries,
    actionLogEntries.length - 1,
    runtimeContext.effectiveContextConfig,
    ai,
    summaryForwardOptions,
    { stage: contextStage, onContextEvent: s.onContextEvent }
  );
  if (!isError) {
    mutableState.runtimeStateSummary =
      await runtimeContext.captureRuntimeStateSummary();
  }
  noteActorTurnErrorState(isError);
  if (await refreshCheckpointSummary(entryTurn)) {
    resetActorModelErrorState();
  }

  if (completionState.payload && 'guidance' in completionState.payload) {
    completionState.payload = undefined;
    return { shouldBreak: false, shouldContinue: true };
  }

  if (completionState.payload) {
    return { shouldBreak: true, shouldContinue: false };
  }

  return { shouldBreak: false, shouldContinue: false };
}
